import {
  addDays,
  differenceInCalendarMonths,
  endOfMonth,
  format,
  isFirstDayOfMonth,
  isLastDayOfMonth,
  isSameDay,
  parse,
  startOfWeek,
} from 'date-fns'

import { dateToString } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import { detectDuration } from './dateUtils'

export const parseBackendDate = (date: string) => parse(date, 'yyyy-MM-dd', new Date())

export const formatDateRange = (
  startDate: Date,
  endDate: Date,
  longMonths = false,
  withRelativeDates = true
) => {
  const today = new Date()

  // Relative to today
  if (withRelativeDates && isSameDay(today, endDate)) {
    return formatRelativeDateRange(startDate, today)
  }

  // Complete years
  if (
    startDate.getMonth() === 0 &&
    isFirstDayOfMonth(startDate) &&
    endDate.getMonth() === 11 &&
    isLastDayOfMonth(endDate)
  ) {
    if (startDate.getFullYear() === endDate.getFullYear()) {
      return startDate.getFullYear().toString()
    }

    return `${startDate.getFullYear()} - ${endDate.getFullYear()}`
  }

  // Complete months
  if (isFirstDayOfMonth(startDate) && isLastDayOfMonth(endDate)) {
    const monthYearFormat = longMonths ? 'MMMM yyyy' : 'MMM yyyy'

    if (
      startDate.getFullYear() === endDate.getFullYear() &&
      startDate.getMonth() === endDate.getMonth()
    ) {
      // Full single month
      return format(startDate, monthYearFormat)
    }

    // Relative to this month
    if (withRelativeDates && isSameDay(endOfMonth(today), endDate)) {
      const monthCount = differenceInCalendarMonths(endDate, startDate) + 1

      return i18n.t('common.date_navigator.range_labels.until_this_month.months', {
        count: monthCount,
      })
    }

    // Full separate months
    return `${format(startDate, monthYearFormat)} - ${format(endDate, monthYearFormat)}`
  }

  // Full week
  if (isSameDay(addDays(startDate, 6), endDate) && isSameDay(startOfWeek(startDate), startDate)) {
    return i18n.t('common.date_navigator.range_labels.full_week', {
      first_day: dateToString(startDate, 'ui_dmy_long'),
    })
  }

  if (isSameDay(startDate, endDate)) {
    return dateToString(startDate, 'ui_dmy_long')
  }

  return `${dateToString(startDate, 'ui_dmy_long')} - ${dateToString(endDate, 'ui_dmy_long')}`
}

const formatRelativeDateRange = (startDate: Date, today: Date) => {
  if (isSameDay(today, startDate)) {
    return i18n.t('common.today')
  }

  const { period, duration } = detectDuration(startDate, today)

  switch (period) {
    case 'day':
      return i18n.tp('common.date_navigator.range_labels.until_today.days', {
        count: duration,
      })

    case 'week':
      return i18n.tp('common.date_navigator.range_labels.until_today.weeks', {
        count: duration,
      })

    case 'month':
      return i18n.tp('common.date_navigator.range_labels.until_today.months', {
        count: duration,
      })

    case 'year':
      return i18n.tp('common.date_navigator.range_labels.until_today.years', {
        count: duration,
      })
  }
}
